import React, { useState } from 'react';
import { Box, Tooltip, IconButton, Typography, alpha } from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';
import { BotMessage } from '../hooks/useChatbotState';
import { processBotMessage } from '../utils/botMessageProcessor';

interface ChatMessageActionsProps {
  message: BotMessage;
  isUser: boolean;
}

export const ChatMessageActions: React.FC<ChatMessageActionsProps> = ({ message, isUser }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    try {
      let textToCopy = message.content;
      if (!isUser) {
        textToCopy = processBotMessage(message.content).text;
      }
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <Box
      className="message-actions"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        opacity: { xs: 1, md: 0 },
        visibility: { xs: 'visible', md: 'hidden' },
        transition: 'opacity 0.2s ease, visibility 0.2s ease',
      }}
    >
      <Tooltip title={copied ? 'Copied!' : 'Copy'}>
        <IconButton
          size="small"
          onClick={handleCopy}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleCopy(e);
            }
          }}
          aria-label={copied ? 'Message copied' : 'Copy message'}
          sx={{
            padding: '4px',
            color: '#9E9E9E',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          {copied ? <CheckIcon sx={{ fontSize: 15, color: '#4CAF50' }} /> : <CopyIcon sx={{ fontSize: 15 }} />}
        </IconButton>
      </Tooltip>
      {copied && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '10px',
            color: '#4CAF50',
            fontWeight: 500,
          }}
        >
          Copied
        </Typography>
      )}
    </Box>
  );
};
